import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { ThemeProvider } from "next-themes";
import App from "./App.tsx";
import "./index.css";

/**
 * Reads a theme token off :root as a usable colour.
 *
 * The tokens are bare HSL triples ("39.6 50.2% 57.5%") for Tailwind's
 * `hsl(var(--token))` convention, so anything outside that convention has to
 * wrap them. The feedback widget is one: it renders in a shadow root, where
 * the page stylesheet does not reach and `var(--primary)` resolves to nothing.
 */
function token(name: string, fallback: string): string {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return raw ? `hsl(${raw})` : fallback;
}

Sentry.init({
  dsn: "https://47d85397fcb50ce6002c91af224ab934@o4508183405789184.ingest.us.sentry.io/4509345496039424",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
    Sentry.feedbackIntegration({
      // No floating launcher. Fixed to the bottom right of every page, it sat
      // on top of whatever the narrowing viewport moved underneath it - on the
      // event screen, the Sign Up button. FeedbackButton attaches this same
      // dialog to a button in the header, which takes up space rather than
      // hovering over other people's.
      autoInject: false,
      colorScheme: "dark",
      // Sentry's own purple is not in the stylesheet, so the palette pass
      // could not have caught it.
      themeDark: {
        background: token("--card", "#161514"),
        foreground: token("--foreground", "#edeae4"),
        accentBackground: token("--primary", "#c9a961"),
        accentForeground: token("--primary-foreground", "#0d0d0c"),
        submitBackground: token("--primary", "#c9a961"),
        submitBackgroundHover: token("--primary", "#c9a961"),
        submitForeground: token("--primary-foreground", "#0d0d0c"),
        submitBorder: token("--primary", "#c9a961"),
        border: token("--border", "#2a2724"),
        inputBackground: token("--background", "#0d0d0c"),
        inputForeground: token("--foreground", "#edeae4"),
        inputBorder: token("--border", "#2a2724"),
        successColor: token("--success", "#5faa72"),
        errorColor: token("--destructive", "#ff6b73"),
      },
    }),
  ],
  replaysSessionSampleRate: 0.3, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

// Dark only, pinned. The provider stays because sonner reads useTheme for its
// toast styling, but forcedTheme means it never follows the device.
createRoot(document.getElementById("root")!).render(
  <ThemeProvider attribute="class" forcedTheme="dark">
    <App />
  </ThemeProvider>,
);
